import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bug, X, Loader2 } from "lucide-react";

export default function ReportBugModal({ open, onClose }) {
  const [state, setState] = useState({ title: "", description: "", page: "", user_action: "" });
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!state.title.trim() || !state.description.trim()) {
      toast.error("Please add a title and description.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/bug-reports", {
        ...state,
        page: state.page || window.location.pathname,
        browser_info: `${navigator.userAgent} · ${window.innerWidth}x${window.innerHeight}`,
      });
      toast.success("Thanks for the report. We'll look at this shortly.");
      setState({ title: "", description: "", page: "", user_action: "" });
      onClose?.();
    } catch {
      toast.error("Could not send report. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="report-bug-modal">
      <div className="absolute inset-0 bg-[#0A2342]/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-white sm:rounded-xl shadow-2xl m-0 sm:m-4 max-h-[92vh] overflow-y-auto">
        <div className="px-5 sm:px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bug className="w-4 h-4 text-[#1D4E89]" />
            <h2 className="font-heading font-bold text-[#0A2342]">Report a bug or request a feature</h2>
          </div>
          <button onClick={onClose} data-testid="bug-close" className="text-[#64748B] hover:text-[#0A2342]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="px-5 sm:px-6 py-5 space-y-4">
          <div>
            <label className="dms-label block mb-2">Issue title *</label>
            <input data-testid="bug-title" value={state.title} onChange={(e) => set("title", e.target.value)} required placeholder="e.g. Reel caption cuts off at 800 chars" className="dms-input" />
          </div>
          <div>
            <label className="dms-label block mb-2">What happened? *</label>
            <textarea data-testid="bug-description" value={state.description} onChange={(e) => set("description", e.target.value)} required rows={4} placeholder="Describe what went wrong, what you saw on screen, any errors." className="dms-textarea" />
          </div>
          <div>
            <label className="dms-label block mb-2">What were you trying to do?</label>
            <textarea data-testid="bug-user-action" value={state.user_action} onChange={(e) => set("user_action", e.target.value)} rows={2} placeholder="e.g. Generate a caption for a new reel" className="dms-textarea" />
          </div>
          <div>
            <label className="dms-label block mb-2">Page or tool (leave blank to auto-detect)</label>
            <input data-testid="bug-page" value={state.page} onChange={(e) => set("page", e.target.value)} placeholder={window.location.pathname} className="dms-input" />
          </div>
          <p className="text-[12px] text-[#94A3B8]">Your email and browser info are attached automatically. Sent to the DMS team.</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="dms-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} data-testid="bug-submit" className="dms-btn-primary">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : "Send report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
